<template>
  <lila-default-template>
    <template #mainContent>
      <div :class="loadingContent ? 'loading large' : ''">
        <form
          v-show="!loadingContent"
          class="container add-professional-page"
          @submit.prevent="submitForm()"
        >
          <h1 class="title">
            {{ isEdit ? "Editar Profissional" : "Adicionar Profissional" }}
          </h1>

          <lila-message :alert-data="alert" />

          <div class="add-professional-form">
            <!-- Nome -->
            <div class="add-professional-field">
              <label class="add-professional-label">Nome do Profissional</label>
              <lila-input
                v-model="v.data.name.$model"
                placeholder="Digite aqui"
                :validation="v.data.name"
              />
            </div>

            <!-- E-mail -->
            <div class="add-professional-field">
              <label class="add-professional-label">E-mail</label>
              <lila-input
                v-model="v.data.email.$model"
                placeholder="Digite aqui"
                :validation="v.data.email"
              />
            </div>

            <!-- Sexo -->
            <div class="add-professional-field">
              <label class="add-professional-label">Sexo</label>
              <lila-select
                v-model="data.gender"
                placeholder="Selecione"
                :options="genderList"
              />
            </div>

            <!-- Equipes (múltipla seleção) -->
            <div class="add-professional-field">
              <label class="add-professional-label">Selecione a Equipe</label>
              <team-tree-checklist
                v-model="data.teamIds"
                :cascade-descendants="true"
                :tree="teamTree"
                :is-selectable="isTeamSelectable"
              />
            </div>

            <!-- POC Cuidados Paliativos (feature 16): CNS, certificação e CBO/horas por equipe -->
            <palliative-professional-profile
              v-if="showPalliativeProfile"
              ref="palliativeProfile"
              :professional-id="professionalId"
              :teams="selectedTeams"
            />

            <!-- Celular -->
            <div class="add-professional-field">
              <label class="add-professional-label">Celular</label>
              <lila-mask
                v-model="data.phone"
                placeholder="(xx) xxxx-xxxx"
                :mask="['(##) ####-####', '(##) #####-####']"
              />
            </div>

            <!-- Flags -->
            <div class="add-professional-flags">
              <lila-checkbox
                v-model="data.isActive"
                title="Ativo"
              />
            </div>

            <!-- Ações -->
            <div class="add-professional-actions">
              <lila-button
                id="save-professional"
                type="submit"
                title="Salvar"
                :loading="loading"
                :second-button="{
                  type: 'route',
                  route: 'professionals',
                  title: 'Cancelar',
                  class: 'cancel'
                }"
              />
            </div>
          </div>
        </form>
      </div>
    </template>
  </lila-default-template>
</template>

<script>
import DefaultTemplate from "@components/template/DefaultTemplate.vue";

import Input from "@components/shared/fields/Input.vue";
import Select from "@components/shared/fields/Select.vue";
import Checkbox from "@components/shared/fields/Checkbox.vue";
import Button from "@components/shared/button/Button.vue";
import Message from "@components/shared/message/Message.vue";
import LilaMask from "@components/shared/fields/LilaMask.vue";
import TeamTreeChecklist from "@components/shared/lila/TeamTreeChecklist.vue";
import PalliativeProfessionalProfile from "@components/management/PalliativeProfessionalProfile.vue";

import { required, email } from "@vuelidate/validators";

import GenderOptions from "@assets/options/gender.json";

// Garante que o nome possua ao menos duas palavras (nome e sobrenome),
// seguindo a mesma regra do cadastro de paciente.
const verifyLastName = function (value) {
  const name = value.trim().replace(/\s\s+/g, " ").split(" ");
  if (typeof name[1] == "undefined" || (typeof name[1] != "undefined" && name[1].length < 1)) return false;
  return true;
};

export default {
  components: {
    "lila-input": Input,
    "lila-select": Select,
    "lila-checkbox": Checkbox,
    "lila-button": Button,
    "lila-message": Message,
    "lila-default-template": DefaultTemplate,
    "team-tree-checklist": TeamTreeChecklist,
    "palliative-professional-profile": PalliativeProfessionalProfile,
    LilaMask
  },
  data() {
    return {
      data: {
        name: "",
        email: "",
        gender: "",
        teamIds: [],
        phone: "",
        isActive: false
      },
      // Unidades e equipes visíveis (api/team/available), cada uma com o Path
      // das unidades ancestrais — base da árvore Unidades › Equipes.
      transferTargets: [],
      // Status carregado da lista: o PUT /status só é chamado se ele mudar.
      initialIsActive: null,
      genderList: GenderOptions.options,
      loading: false,
      loadingContent: false,
      alert: {
        message: "",
        type: "",
        show: false
      }
    };
  },
  validations() {
    return {
      data: {
        name: {
          required,
          verifyLastName
        },
        email: {
          required,
          email
        }
      }
    };
  },
  computed: {
    professionalId() {
      return this.$route.params.id;
    },
    isEdit() {
      return !!this.professionalId;
    },
    // POC Cuidados Paliativos: cadastro e edição, só com a feature 16.
    showPalliativeProfile() {
      return this.$Utilities.hasPalliativeCareAccess(this);
    },
    // Equipes marcadas, com o caminho Unidade › Equipe para o bloco de cuidados paliativos.
    selectedTeams() {
      return this.data.teamIds.map((id) => {
        const target = this.transferTargets.find((t) => String(t.id) === String(id));
        if (!target) return { id, label: `Equipe ${id}` };
        const path = (target.path || []).map((unit) => unit.name);
        return { id, label: [...path, target.name].join(" › ") };
      });
    },
    // Árvore Unidades › Equipes montada a partir do Path de cada equipe.
    // Mesma regra da listagem de profissionais (Professionals.vue): sem Path,
    // a equipe cai na raiz e a lista fica plana.
    teamTree() {
      const roots = [];
      const findOrAdd = (children, node) => {
        let existing = children.find((c) => String(c.id) === String(node.id));
        if (!existing) {
          existing = { id: node.id, name: node.name, children: [] };
          children.push(existing);
        }
        return existing;
      };
      this.transferTargets.forEach((target) => {
        let children = roots;
        (target.path || []).forEach((unit) => {
          children = findOrAdd(children, unit).children;
        });
        findOrAdd(children, target);
      });
      return roots;
    }
  },
  created() {
    this.getTransferTargets();
    if (this.isEdit) this.loadProfessional();
  },
  methods: {
    authHeader() {
      return {
        Authorization: `bearer ${
          this.$Session.get(this.$userSessionName).token.Value
        }`
      };
    },

    // Carrega o profissional a partir do item passado pela listagem
    // (não há GET api/professional/{id}; os dados vêm da lista).
    loadProfessional() {
      let item = null;
      try {
        const cached = sessionStorage.getItem("lila.editProfessional");
        if (cached) item = JSON.parse(cached);
      } catch (e) {
        item = null;
      }

      const cachedId = item
        ? this.$Utilities.getValueIgnoreCase(item, "Id")
        : null;

      // Sem dados (acesso direto pela URL / refresh) ou id divergente:
      // volta para a listagem, que tem os dados carregados.
      if (!item || String(cachedId) !== String(this.professionalId)) {
        this.$router.replace({ name: "professionals" });
        return;
      }

      const gv = (key) => this.$Utilities.getValueIgnoreCase(item, key);
      this.data.name = gv("Name") || "";
      this.data.email = gv("Email") || "";
      this.data.gender = gv("Gender") || "";
      this.data.phone = gv("Phone") || "";
      // A API devolve Status ("Active"/"Inactive"/"Pending"), não IsActive.
      this.data.isActive =
        (gv("Status") ?? "Active").toString().toLowerCase() === "active";
      this.initialIsActive = this.data.isActive;
      this.data.teamIds = this.extractTeamIds(item);
    },

    // Extrai os ids das equipes tolerando os formatos que o backend pode
    // retornar (Teams/Doctors como array de objetos {Id} ou DoctorsId escalar).
    extractTeamIds(item) {
      const raw =
        this.$Utilities.getValueIgnoreCase(item, "Teams") ||
        this.$Utilities.getValueIgnoreCase(item, "Doctors") ||
        this.$Utilities.getValueIgnoreCase(item, "DoctorsId") ||
        [];
      const list = Array.isArray(raw) ? raw : [raw];
      return list
        .map((team) => {
          if (team == null) return null;
          if (typeof team === "object") {
            return this.$Utilities.getValueIgnoreCase(team, "Id");
          }
          return team;
        })
        .filter((id) => id != null);
    },

    isTeamSelectable(id) {
      return this.transferTargets.some((target) => String(target.id) === String(id));
    },

    // Unidades e equipes disponíveis para vínculo, escopadas por role no backend e já
    // com as unidades ancestrais (Path) para montar a hierarquia.
    getTransferTargets() {
      this.$axios
        .get("api/team/available", { headers: this.authHeader() })
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : res.data.Data || [];
          this.transferTargets = list.filter(Boolean).map((item) => ({
            id: this.$Utilities.getValueIgnoreCase(item, "Id"),
            name: this.$Utilities.getValueIgnoreCase(item, "Name"),
            path: (
              this.$Utilities.getValueIgnoreCase(item, "Path") || []
            ).map((unit) => ({
              id: this.$Utilities.getValueIgnoreCase(unit, "Id"),
              name: this.$Utilities.getValueIgnoreCase(unit, "Name")
            }))
          }));
        })
        .catch((error) => {
          if (error.response) this.$Utilities.verifyToken(error.response.status);
          console.error("Erro ao carregar equipes:", error);
        });
    },

    // POC Cuidados Paliativos: devolve a mensagem de erro, ou "" se salvou.
    async savePalliativeProfile(profile, professionalId) {
      try {
        await profile.save(professionalId);
        return "";
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível salvar o perfil do profissional.", error);
        return error.message || "erro desconhecido.";
      }
    },
    async submitForm() {
      this.alert.show = false;

      this.v.$touch();
      const palliativeProfile = this.$refs.palliativeProfile;
      const palliativeProfileValid = palliativeProfile ? palliativeProfile.validate() : true;
      if (this.v.$invalid || !palliativeProfileValid) return;

      this.loading = true;

      // Contrato do POST/PUT /api/Professional. O update NÃO altera o status —
      // ativar/inativar é o endpoint dedicado PUT api/Professional/{id}/status.
      const professional = {
        name: this.data.name,
        email: this.data.email,
        gender: this.data.gender,
        phone: this.data.phone,
        teamsId: this.data.teamIds
      };

      const auth = {
        headers: {
          Authorization: `bearer ${
            this.$Session.get(this.$userSessionName).token.Value
          }`
        }
      };

      // POC Cuidados Paliativos: o profissional é salvo mesmo que o perfil falhe; o aviso vai na lista.
      let profileError = "";

      try {
        if (this.isEdit) {
          // Os dois updates são independentes: assim o status (ativar/inativar)
          // é atualizado mesmo que o update dos dados falhe (e vice-versa).
          const errors = [];

          // 1) Dados do profissional (nome, e-mail, telefone, equipes).
          try {
            await this.$axios.put(
              `api/Professional/${this.professionalId}`,
              professional,
              auth
            );
          } catch (e) {
            errors.push(e);
          }

          // 2) Status (Active/Inactive) via endpoint dedicado, só se mudou.
          // A API recusa alterar o próprio status, mesmo sem mudança.
          if (this.data.isActive !== this.initialIsActive) {
            try {
              await this.$axios.put(
                `api/Professional/${this.professionalId}/status`,
                { Status: this.data.isActive ? "Active" : "Inactive" },
                auth
              );
            } catch (e) {
              errors.push(e);
            }
          }

          if (errors.length) throw errors[0];

          // 3) POC Cuidados Paliativos: perfil SUS (CNS, certificação, CBO e horas por equipe).
          if (palliativeProfile) profileError = await this.savePalliativeProfile(palliativeProfile);
        } else {
          const res = await this.$axios.post("api/Professional", professional, auth);

          // POC Cuidados Paliativos: o id só existe depois do cadastro.
          // Resposta: { professional: { Id, ... }, message }.
          if (palliativeProfile) {
            const created = this.$Utilities.getValueIgnoreCase(res.data || {}, "professional");
            const createdId = created ? this.$Utilities.getValueIgnoreCase(created, "Id") : null;
            if (createdId) profileError = await this.savePalliativeProfile(palliativeProfile, createdId);
            else profileError = "a resposta do cadastro não trouxe o id do profissional.";
          }
        }

        const savedMessage = this.isEdit
          ? "Profissional atualizado com sucesso."
          : "Profissional cadastrado com sucesso.";
        this.$router.push({
          name: "professionals",
          query: {
            customMessageType: profileError ? "danger" : "success",
            customMessage: profileError
              ? `${savedMessage} O perfil de cuidados paliativos não foi salvo: ${profileError}`
              : savedMessage
          }
        });
      } catch (error) {
        console.error("API Error:", error);

        this.alert.type = "warning";

        if (error.response && error.response.data) {
          const apiMessage =
            error.response.data.Message || "Erro ao salvar profissional.";
          if (
            error.response.data.Errors &&
            Array.isArray(error.response.data.Errors)
          ) {
            this.alert.message = `${apiMessage} Detalhes: ${error.response.data.Errors.join(
              " "
            )}`;
          } else {
            this.alert.message = apiMessage;
          }
          this.$Utilities.controlAlert(this.alert.message, this.alert);
        } else if (error.message) {
          this.alert.message = error.message;
          this.$Utilities.controlAlert(this.alert.message, this.alert);
        } else {
          this.alert.message = "Ocorreu um erro desconhecido.";
          this.$Utilities.controlAlert(this.alert.message, this.alert);
        }

        this.$Utilities.verifyToken(error.response ? error.response.status : null);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss">
$lila-primary: #4f3f82;

.add-professional-page {
  padding-top: 10px;
}

.add-professional-page .title {
  color: $lila-primary;
  font-weight: 700;
  margin-bottom: 24px;
}

.add-professional-page .add-professional-form {
  max-width: 480px;
}

.add-professional-page .add-professional-field {
  margin-bottom: 18px;
}

.add-professional-page .add-professional-label {
  display: block;
  font-size: 13px;
  color: #6b6b6b;
  margin-bottom: 8px;
}

/* Flags (Ativo) */
.add-professional-page .add-professional-flags {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 22px 0 26px;
}

.add-professional-page .add-professional-actions {
  margin-top: 8px;
  margin-bottom: 40px;
}
</style>
